import {
  type CreateMaintenanceInstallationRequest,
  createMaintenanceInstallationRequestSchema,
  type MaintenanceInstallation,
} from '@gb-ticket/shared-types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { type Resolver, FormProvider, useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import {
  MaintenanceInstallationIdentityFields,
  MaintenanceInstallationTechnicalFields,
} from '@/products/maintenance/features/forms/installation-form';
import { MaintenanceFormPage } from '@/products/maintenance/features/maintenance-form-page';
import {
  useCreateMaintenanceInstallation,
  useMaintenanceInstallation,
  useMaintenanceProject,
  useUpdateMaintenanceInstallation,
} from '@/products/maintenance/features/use-maintenance';
import {
  maintenanceInstallationPath,
  maintenanceProjectPath,
} from '@/products/maintenance/maintenance-paths';

const emptyInstallationForm = (projectId: string): CreateMaintenanceInstallationRequest => ({
  projectId,
  installationNumber: '',
  name: '',
  installationType: '',
  manufacturer: null,
  model: null,
  serialNumber: null,
  location: '',
  commissionedOn: null,
  isActive: true,
  metadata: null,
});

function installationToFormValues(
  row: MaintenanceInstallation,
): CreateMaintenanceInstallationRequest {
  return {
    projectId: row.projectId,
    installationNumber: row.installationNumber,
    name: row.name,
    installationType: row.installationType,
    manufacturer: row.manufacturer,
    model: row.model,
    serialNumber: row.serialNumber,
    location: row.location,
    commissionedOn: row.commissionedOn,
    isActive: row.isActive,
    metadata: row.metadata,
  };
}

export function MaintenanceInstallationFormPage() {
  const { projectId: routeProjectId = '', installationId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isEdit = location.pathname.endsWith('/bearbeiten');
  const isCreate = location.pathname.endsWith('/neu');

  usePageTitle(isEdit ? 'Anlage bearbeiten' : 'Anlage anlegen');

  const project = useMaintenanceProject(isCreate ? routeProjectId : '');
  const installation = useMaintenanceInstallation(isEdit ? installationId : '');
  const create = useCreateMaintenanceInstallation();
  const update = useUpdateMaintenanceInstallation(installationId);
  const pending = create.isPending || update.isPending;

  const form = useForm<CreateMaintenanceInstallationRequest>({
    resolver: zodResolver(
      createMaintenanceInstallationRequestSchema,
    ) as Resolver<CreateMaintenanceInstallationRequest>,
    defaultValues: emptyInstallationForm(routeProjectId),
  });

  useEffect(() => {
    if (isEdit && installation.data) {
      form.reset(installationToFormValues(installation.data));
    }
  }, [form, installation.data, isEdit]);

  if (!isCreate && !isEdit) {
    return null;
  }

  if (isCreate && project.isPending) {
    return <p className="text-sm text-muted-foreground">Projekt wird geladen …</p>;
  }
  if (isCreate && (project.isError || !project.data)) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Das Projekt wurde nicht gefunden.
      </p>
    );
  }
  if (isEdit && installation.isPending) {
    return <p className="text-sm text-muted-foreground">Anlage wird geladen …</p>;
  }
  if (isEdit && (installation.isError || !installation.data)) {
    return (
      <p className="text-sm text-destructive" role="alert">
        Die Anlage wurde nicht gefunden.
      </p>
    );
  }

  const cancelHref = isEdit
    ? maintenanceInstallationPath(installationId)
    : maintenanceProjectPath(routeProjectId);

  async function onValid(values: CreateMaintenanceInstallationRequest) {
    try {
      if (isEdit) {
        const saved = await update.mutateAsync({
          installationNumber: values.installationNumber,
          name: values.name,
          installationType: values.installationType,
          manufacturer: values.manufacturer,
          model: values.model,
          serialNumber: values.serialNumber,
          location: values.location,
          commissionedOn: values.commissionedOn,
          isActive: values.isActive,
          metadata: values.metadata,
        });
        toast.success('Anlage gespeichert.');
        void navigate(maintenanceInstallationPath(saved.id));
        return;
      }
      const created = await create.mutateAsync(values);
      toast.success('Anlage angelegt.');
      void navigate(maintenanceInstallationPath(created.id));
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : 'Die Anlage konnte nicht gespeichert werden.';
      toast.error(message);
    }
  }

  const projectName = isCreate ? project.data?.name : installation.data?.project.name;

  return (
    <FormProvider {...form}>
      <MaintenanceFormPage
        title={isEdit ? 'Anlage bearbeiten' : 'Anlage anlegen'}
        description={projectName ? `Projekt: ${projectName}` : undefined}
        cancelHref={cancelHref}
        pending={pending}
        onSubmit={() => {
          void form.handleSubmit(onValid)();
        }}
      >
        <MaintenanceInstallationIdentityFields />
        <MaintenanceInstallationTechnicalFields />
      </MaintenanceFormPage>
    </FormProvider>
  );
}
